import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Eye, EyeOff, CheckCircle2, X, BadgeCheck, Phone, Copy, Send, ShieldCheck, Loader2, MessageCircle } from 'lucide-react';

const PANEL_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #22FF88))';
const PANEL_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 70%, #FFFFFF)';
const PANEL_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 16%, transparent)';
const PANEL_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 48%, transparent)';
const PANEL_GOLD = 'var(--vl-brand-primary, #D4AF37)';
const PANEL_GOLD_DIM = 'color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 18%, transparent)';
const PANEL_GOLD_BORDER = 'color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 52%, transparent)';

const inputStyle = {
  background: 'color-mix(in oklab, var(--vl-bg, #000000) 86%, #06130D)',
  borderColor: PANEL_ACCENT_BORDER,
  color: '#FFFFFF',
  caretColor: PANEL_ACCENT,
  boxShadow: `0 0 0 1px ${PANEL_ACCENT_DIM}`,
};

const toUsDigits = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length > 10 && digits.startsWith('1')) return digits.slice(1, 11);
  return digits.slice(0, 10);
};

const formatUsPhone = (digits = '') => {
  const d = toUsDigits(digits);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const cleanSmsNumber = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};

/**
 * In-app password change form. Validates current/new locally then calls
 * /api/auth/change-password.
 */
export function ResetPasswordPanel({ close }) {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current || !newPw) { toast.error('Fill all fields'); return; }
    if (newPw !== confirm) { toast.error('New passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Min 6 characters'); return; }
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', { old_password: current, new_password: newPw });
      toast.success('Password changed');
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Current Password</label>
        <div className="relative">
          <Input type={showCur ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} className="pr-10" style={inputStyle} data-testid="reset-current" />
          <button onClick={() => setShowCur(!showCur)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#FFFFFF' }}>
            {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>New Password</label>
        <div className="relative">
          <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="pr-10" style={inputStyle} data-testid="reset-new" />
          <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#FFFFFF' }}>
            {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Confirm New Password</label>
        <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} data-testid="reset-confirm" />
      </div>
      <Button onClick={submit} disabled={busy} className="btn-primary w-full h-11 mt-2" data-testid="reset-submit">
        {busy ? <div className="spinner w-4 h-4" /> : 'Change Password'}
      </Button>
    </div>
  );
}

/**
 * In-app profile editor — full name, username (debounced uniqueness check),
 * phone. Email cannot be changed here.
 */
export function EditProfilePanel({ user, checkAuth, close, initialVerifyOpen = false }) {
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [usernameAvail, setUsernameAvail] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState(() => toUsDigits(user?.phone_number || user?.phone_e164 || user?.phone || ''));
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [reverseSms, setReverseSms] = useState(null);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [verifyPopupOpen, setVerifyPopupOpen] = useState(initialVerifyOpen);
  const [busy, setBusy] = useState(false);
  const phoneInputRef = useRef(null);
  const currentUsername = (user?.username || '').trim();
  const phoneVerified = !!(verifyStatus?.verified || user?.phone_verified || user?.phone_verified_at);
  const phoneE164 = phoneDigits ? `+1${phoneDigits}` : '';

  const loadVerifyStatus = async () => {
    try {
      const r = await api.get('/api/users/me/phone-verification/status');
      setVerifyStatus(r.data || null);
      return r.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    loadVerifyStatus();
  }, []);

  useEffect(() => {
    if (!initialVerifyOpen || phoneVerified) return;
    window.setTimeout(() => phoneInputRef.current?.focus?.(), 150);
    if (verifyStatus?.reverse_sms_enabled && phoneDigits.length === 10) {
      setVerifyPopupOpen(true);
    }
  }, [initialVerifyOpen, verifyStatus?.reverse_sms_enabled, phoneVerified, phoneDigits.length]);

  useEffect(() => {
    if (currentUsername) {
      setUsernameAvail(null);
      setUsernameChecking(false);
      return undefined;
    }
    const current = (user?.username || '').toLowerCase();
    const next = (username || '').trim().toLowerCase();
    if (!next || next === current) { setUsernameAvail(null); return; }
    if (next.length < 3 || !/^[a-zA-Z0-9_]+$/.test(next)) { setUsernameAvail(false); return; }
    setUsernameChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/api/auth/username/available?username=${encodeURIComponent(next)}`);
        setUsernameAvail(!!r.data?.available);
      } catch { setUsernameAvail(null); }
      finally { setUsernameChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [username, user?.username, currentUsername]);

  const submit = async () => {
    if (phoneDigits && phoneDigits.length !== 10) {
      toast.error('Enter a valid US phone number');
      return;
    }
    setBusy(true);
    try {
      await api.patch('/api/auth/profile', {
        full_name: fullName.trim(),
        phone_number: phoneE164,
      });
      const nextU = (username || '').trim();
      if (!currentUsername && nextU && nextU.toLowerCase() !== (user?.username || '').toLowerCase()) {
        await api.post('/api/auth/username/change', { new_username: nextU });
        toast.success(`Username set to @${nextU}`);
      } else {
        toast.success('Profile updated');
      }
      await checkAuth();
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save');
    } finally { setBusy(false); }
  };

  const usernameChanged = !currentUsername && (username || '').toLowerCase() !== (user?.username || '').toLowerCase();

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const startReverseSms = async () => {
    if (phoneDigits.length !== 10) {
      toast.error('Enter your 10 digit US phone number');
      return;
    }
    setReverseBusy(true);
    try {
      const r = await api.post('/api/users/me/phone-verification/reverse/start', { phone: phoneE164 });
      setReverseSms(r.data);
      setVerifyPopupOpen(true);
      toast.success('Code ready. Open SMS or copy it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start SMS verification');
    } finally {
      setReverseBusy(false);
    }
  };

  useEffect(() => {
    if (!reverseSms || phoneVerified) return undefined;
    const t = setInterval(async () => {
      const status = await loadVerifyStatus();
      if (status?.verified) {
        toast.success('Phone verified. $5 Freeplay added.');
        setReverseSms(null);
        setVerifyPopupOpen(false);
        await checkAuth?.();
      }
    }, 5000);
    return () => clearInterval(t);
  }, [reverseSms, phoneVerified, checkAuth]);

  const openSmsApp = () => {
    if (!reverseSms?.code) {
      toast.error('Generate a code first');
      return;
    }
    const to = cleanSmsNumber(reverseSms.receiving_number || verifyStatus?.reverse_sms_receiving_number || '');
    if (!to) {
      toast.error('Receiving number missing');
      return;
    }
    const separator = /iPad|iPhone|iPod/i.test(navigator.userAgent || '') ? '&' : '?';
    window.location.href = `sms:${to}${separator}body=${encodeURIComponent(reverseSms.code)}`;
  };

  const copySmsBundle = async () => {
    if (!reverseSms?.code) return;
    const to = reverseSms.receiving_number || verifyStatus?.reverse_sms_receiving_number || '';
    await copyText(`Send ${reverseSms.code} to ${to}`, 'SMS details');
  };

  const openVerifyPopup = () => {
    if (phoneDigits.length !== 10) {
      phoneInputRef.current?.focus?.();
      toast.info('Add your 10 digit number first');
      return;
    }
    setVerifyPopupOpen(true);
  };

  return (
    <div className="space-y-3" data-testid="edit-profile-panel">
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Full name</label>
        <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" style={inputStyle} data-testid="edit-fullname" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Username</label>
        <div className="relative">
          <Input
            value={username}
            onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            placeholder="preferred_name"
            style={inputStyle}
            maxLength={24}
            disabled={!!currentUsername}
            readOnly={!!currentUsername}
            data-testid="edit-username"
          />
          {usernameChanged && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {usernameChecking ? <div className="spinner w-4 h-4" /> :
                usernameAvail === true ? <CheckCircle2 className="w-5 h-5 text-green-400" data-testid="username-avail-ok" /> :
                usernameAvail === false ? <X className="w-5 h-5 text-red-400" data-testid="username-avail-bad" /> : null}
            </span>
          )}
        </div>
        <p className="text-[10px] mt-1" style={{ color: usernameAvail === false ? '#F87171' : 'rgba(255,255,255,0.72)' }}>
          {currentUsername ? 'Username is locked once set. Tap it on your profile to copy it.' :
           usernameChanged && usernameAvail === false ? 'Taken or invalid — use 3-24 letters / digits / _' :
           usernameChanged && usernameAvail === true ? 'Available! Save to claim it.' :
           'Pick a unique handle. You cannot change it after saving.'}
        </p>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Phone</label>
        <div className="flex items-center gap-2">
          <span
            className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #38BDF8, #2563EB)',
              boxShadow: '0 0 18px rgba(56,189,248,0.48), inset 0 1px 0 rgba(255,255,255,0.28)',
            }}
            aria-hidden
          >
            <Phone className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={2.5} />
          </span>
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-black text-sky-200 select-none">+1</span>
            <Input
              ref={phoneInputRef}
              value={formatUsPhone(phoneDigits)}
              onChange={e => setPhoneDigits(toUsDigits(e.target.value))}
              placeholder="425 835 3943"
              className="pl-11 pr-24"
              style={inputStyle}
              inputMode="numeric"
              type="tel"
              data-testid="edit-phone"
            />
            {phoneVerified ? (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-lg flex items-center gap-1 text-[11px] font-bold bg-emerald-500/15 text-emerald-200 border border-emerald-400/30">
                <CheckCircle2 className="w-3.5 h-3.5" /> Verified
              </span>
            ) : verifyStatus?.reverse_sms_enabled ? (
              <button
                type="button"
                onClick={openVerifyPopup}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-3 rounded-lg text-[11px] font-black bg-sky-400 text-black shadow-[0_0_14px_rgba(56,189,248,0.45)]"
                data-testid="phone-verify-open"
              >
                Verify
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {phoneVerified && (
        <div className="rounded-lg p-2 text-[11px] flex items-center gap-2" style={{ background: PANEL_ACCENT_DIM, color: '#FFFFFF', border: `1px solid ${PANEL_ACCENT_BORDER}` }}>
          <CheckCircle2 className="w-4 h-4" /> Phone verified
        </div>
      )}
      <div className="rounded-lg p-2 text-[11px]" style={{ background: PANEL_GOLD_DIM, color: '#FFFFFF', border: `1px solid ${PANEL_GOLD_BORDER}`, boxShadow: `0 0 18px color-mix(in oklab, ${PANEL_GOLD} 16%, transparent)` }}>
        Email cannot be changed here. To reset your password, use Reset Pass in the profile menu.
      </div>
      <Button onClick={submit} disabled={busy || (usernameChanged && usernameAvail === false)} className="btn-primary w-full h-11 display font-bold mt-1" data-testid="edit-profile-submit">
        {busy ? <div className="spinner w-4 h-4" /> : <><BadgeCheck className="w-4 h-4 mr-2" /> Save changes</>}
      </Button>

      {verifyPopupOpen && !phoneVerified && verifyStatus?.reverse_sms_enabled && (
        <div
          className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm px-0 sm:px-4"
          data-testid="reverse-sms-player-popup"
        >
          <div
            className="w-full sm:max-w-md rounded-t-[30px] sm:rounded-[30px] p-5 shadow-2xl border relative overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(8,18,32,0.98), rgba(1,8,14,0.98))',
              borderColor: 'rgba(56,189,248,0.32)',
              boxShadow: '0 -20px 60px rgba(0,0,0,0.6), 0 0 34px rgba(56,189,248,0.22)',
            }}
          >
            <button
              type="button"
              onClick={() => setVerifyPopupOpen(false)}
              className="absolute right-4 top-4 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/15"
              aria-label="Close phone verification"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <div className="flex items-start gap-3 pr-12">
              <span
                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #38BDF8, #2563EB)',
                  boxShadow: '0 0 22px rgba(56,189,248,0.55), inset 0 1px 0 rgba(255,255,255,0.32)',
                }}
              >
                <Phone className="w-7 h-7 text-white" strokeWidth={2.6} />
              </span>
              <div>
                <p className="display text-xl font-black text-white">Verify your number</p>
                <p className="text-xs text-sky-100/75 mt-1">
                  Text the code from <span className="font-bold text-white">+1 {formatUsPhone(phoneDigits)}</span> to our Starlight inbox.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl p-3" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)' }}>
              <div className="flex items-center gap-2 text-xs text-sky-100/80">
                <ShieldCheck className="w-4 h-4 text-sky-300" />
                Country is locked to United States. The sending number must match this profile number.
              </div>
            </div>

            {reverseSms ? (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  <div className="rounded-2xl bg-black/35 border border-white/10 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="uppercase tracking-[0.2em] text-[10px] text-sky-200/60">Text this code</div>
                        <div className="font-mono text-3xl font-black text-sky-200 tracking-widest">{reverseSms.code}</div>
                      </div>
                      <button type="button" onClick={() => copyText(reverseSms.code, 'Code')} className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center">
                        <Copy className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-black/35 border border-white/10 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="uppercase tracking-[0.2em] text-[10px] text-sky-200/60">Send to</div>
                        <div className="font-mono text-base font-bold text-white">{reverseSms.receiving_number}</div>
                      </div>
                      <button type="button" onClick={() => copyText(reverseSms.receiving_number, 'Receiving number')} className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center">
                        <Copy className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={copySmsBundle} className="h-11 bg-white/10 hover:bg-white/15 text-white font-bold">
                    <Copy className="w-4 h-4 mr-2" /> Copy all
                  </Button>
                  <Button type="button" onClick={openSmsApp} className="h-11 bg-sky-400 hover:bg-sky-300 text-black font-black" data-testid="reverse-sms-open-app">
                    <MessageCircle className="w-4 h-4 mr-2" /> Open SMS
                  </Button>
                </div>
                <p className="text-[11px] text-center text-sky-100/62">
                  Waiting for the text. This popup closes automatically after verification.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <Button onClick={startReverseSms} disabled={reverseBusy} className="w-full h-12 bg-sky-400 hover:bg-sky-300 text-black font-black" data-testid="reverse-sms-start-btn">
                  {reverseBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Get text code
                </Button>
                <p className="text-[11px] text-center text-white/50">
                  After the code is created, tap Open SMS to prefill the inbox number and code.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
